import getCompanyOptionApi from '@/api/get/company/company-options/get-company-option';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import uploadToTempS3File from '@/utils/upload-to-s3-file';

export const KEYS_TO_COMPARE = [
  'name',
  'selectedPriceId',
  'status',
  'description',
  'quote_description',
  'brochure_link',
  'video_link',
  'thumbnail',
  'media',
];

/**
 * Check if object is a custom file object with required properties
 * @param {object} obj - Object to check
 * @returns {boolean} True if object is a custom file object
 */
export const isCustomFileObject = (obj) =>
  obj &&
  typeof obj === 'object' &&
  'fileName' in obj &&
  'file' in obj &&
  'fileType' in obj;

/**
 * Compare two values for equality with special handling for file objects and arrays
 * @param {any} val1 - First value to compare
 * @param {any} val2 - Second value to compare
 * @param {string} key - Key name for context-specific comparison
 * @returns {boolean} True if values are equal
 */
export const areValuesEqual = (val1, val2, key) => {
  // Special handling for file fields (thumbnail and media)
  if (key === 'thumbnail' || key === 'media') {
    // Both are custom file objects - compare their properties
    if (isCustomFileObject(val1) && isCustomFileObject(val2)) {
      return (
        val1.fileName === val2.fileName &&
        val1.file === val2.file &&
        val1.fileType === val2.fileType
      );
    }
    // Both are native File objects - compare their properties
    if (val1 instanceof File && val2 instanceof File) {
      return (
        val1.name === val2.name &&
        val1.size === val2.size &&
        val1.type === val2.type &&
        val1.lastModified === val2.lastModified
      );
    }
    // Handle arrays (like media array)
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) {
        return false;
      }
      // Deep compare array items
      return val1.every((item, index) => {
        const item2 = val2[index];
        if (typeof item === 'object' && typeof item2 === 'object') {
          return JSON.stringify(item) === JSON.stringify(item2);
        }
        return item === item2;
      });
    }
  }
  // Regular comparison for all other cases (strings, numbers, booleans, etc.)
  return val1 === val2;
};

/**
 * Creates an array of overridden fields by comparing variant values with original values
 * @param {object} variant - The variant object containing the current values
 * @param {object} originalVariant - The original variant object to compare against
 * @returns {Array<string>} Array of field names that have been overridden
 */
export const createOverridesFromComparison = (variant, originalVariant) => {
  const overrides = [];

  KEYS_TO_COMPARE.forEach((key) => {
    const variantValue = variant[key];
    const originalValue = originalVariant[key];

    // Check if variant value is meaningful (not empty/null/undefined)
    const hasVariantValue =
      (key === 'thumbnail' ? true : variantValue !== null) &&
      variantValue !== undefined &&
      variantValue !== '' &&
      !(Array.isArray(variantValue) && variantValue.length === 0);

    // If there's a meaningful variant value and it's different from original
    if (hasVariantValue && !areValuesEqual(variantValue, originalValue, key)) {
      overrides.push(key);
    }
  });

  return overrides;
};

/**
 * Process thumbnail upload - handles file upload or keeps existing thumbnail
 * @param {object|string} thumbnail - Thumbnail object with file data or existing URL
 * @returns {Promise<string|null>} Processed thumbnail ID/URL or null on failure
 */
export const processThumbnailUpload = async (thumbnail) => {
  // Check if thumbnail has a new file to upload
  if (thumbnail?.file) {
    const { file, fileName, fileType } = thumbnail;

    // Generate unique URL with timestamp and random number to avoid conflicts
    const uniqueId = `${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const url = `image-upload/${uniqueId}-${fileName}`;

    // Upload file to S3 and return the file ID
    const data = await uploadToTempS3Base64(file, url, fileType);
    return data.id;
  }
  if (thumbnail) {
    // Keep existing thumbnail if no new file (for edits)
    return thumbnail;
  }
  // No thumbnail provided
  return null;
};

/**
 * Process gallery images upload - handles multiple image files
 * @param {Array} productGallery - Array of gallery items to process
 * @returns {Promise<Array>} Array of processed gallery items with S3 URLs
 */
export const processGalleryUpload = async (productGallery) => {
  // Return empty array if no gallery data provided
  if (!productGallery || !Array.isArray(productGallery)) {
    return [];
  }

  // Process each gallery item asynchronously
  const newGallery = productGallery.map(async (gallery, index) => {
    if (!gallery) return null;

    const { file } = gallery;

    // Handle new file uploads
    if (file) {
      // Generate unique URL with timestamp and random number to avoid conflicts
      return uploadToTempS3File(file, `image-upload/${file.name}`).then(
        (data) => {
          const fileKey = data?.id;
          // Return structured gallery object
          const updatedGallery = {
            image: fileKey,
            order: index,
          };
          return updatedGallery;
        },
      );
    }

    // Handle existing gallery items (for edits)
    if (gallery.image || gallery.id) {
      // Keep existing media items that already have image URLs or IDs
      return {
        image: gallery.image,
        order: gallery.order ?? index, // Use existing order or fallback to index
        ...(gallery.id && { id: gallery.id }), // Preserve existing ID if present
        ...(gallery.option_price_link_id && {
          option_price_link_id: gallery.option_price_link_id,
        }),
      };
    }

    return null;
  });

  // Wait for all uploads to complete
  const addGallery = await Promise.all(newGallery);

  // Filter out null values (failed uploads or empty items)
  return addGallery.filter(Boolean);
};

/**
 * Process option data for form initialization - loads existing data for edit/duplicate or returns defaults
 * @param {string} companyId - Company ID
 * @param {string} optionId - Option ID (for edit) or duplicate ID (for new)
 * @param {object} options - Configuration options
 * @param {boolean} options.isEdit - Whether this is for edit mode
 * @param {boolean} options.isDuplicate - Whether this is for duplicating an option
 * @param {Function} options.setInitialPrices - Function to set initial prices (for edit mode)
 * @param {string} options.selectedParentId - ID of selected parent for historical data migration
 * @returns {Promise<object>} Processed option data with form defaults
 */
export const processOptionData = async (companyId, optionId, options = {}) => {
  const {
    isEdit = false,
    isDuplicate = false,
    selectedParentId = null,
  } = options;

  // For new options without duplication, return empty defaults
  if (!isEdit && !isDuplicate) {
    return {
      prices: [],
      variant: [],
    };
  }

  // Fetch existing option data from API
  const option = await getCompanyOptionApi(companyId, optionId);
  const prices = option.data.prices ?? [];
  const optionData = option.data.option ?? {};

  // Delete ID for both edit and duplicate scenarios to prevent conflicts
  delete option.data.option.id;
  delete option.data.option.created_at;
  delete option.data.option.updated_at;

  // Separate parent options from variants
  // Parent options have parent_option = null, variants have a parent_option value
  const parent = selectedParentId
    ? prices.filter(
        (price) =>
          price.parent_option === null && selectedParentId === price.id,
      )
    : prices.filter((price) => price.parent_option === null);

  const variant = selectedParentId
    ? prices.filter(
        (price) => price.parent_option || selectedParentId !== price.id,
      )
    : prices.filter((price) => price.parent_option);

  // Set selected price IDs for multi-select dropdowns
  const selectedPriceIds = prices.map((price) => price.price_id);

  // Get the parent price ID
  const parentPriceId = prices.find((price) => price.parent_option === null);

  // Start with basic option data
  let processedOption = {
    ...optionData,
    selectedPriceId: parentPriceId?.price_id,
  };

  let parentPrice = parent[0];

  if (selectedParentId) {
    parentPrice = parent.find((price) => price.id === selectedParentId);
  }
  // For edit mode and duplicate mode, merge data from first price
  if (isEdit || isDuplicate) {
    let media = parentPrice?.gallery || optionData?.media || [];
    if (isDuplicate) {
      media = media.map((m) => ({
        ...m,
        id: 'duplicate',
      }));
    }

    processedOption = {
      ...processedOption,
      option_price_link_id: parentPrice?.id,
      quote_description:
        parentPrice?.quote_description || optionData?.quote_description || null,
      brochure_link:
        parentPrice?.brochure_link || optionData?.brochure_link || null,
      video_link: parentPrice?.video_link || optionData?.media || null,
      media,
    };

    // Replace historic data with the correct data from the parent option.
    if (selectedParentId) {
      processedOption.name =
        parentPrice?.description || optionData?.name || null;
      processedOption.description = parentPrice?.description || null;
      processedOption.thumbnail =
        optionData?.thumbnail || parentPrice?.thumbnail || null;
      processedOption.id = parentPrice?.id || optionData?.id || null;
      processedOption.selectedPriceId = parentPrice?.price_id;
    }
  }
  // Create lookup map for parts by price_id for efficient access
  const partsLookup = new Map(
    option?.data?.option?.parts?.map((part) => [part.price_id, part]) || [],
  );

  // Create variant defaults from existing variant prices
  const variantDefaults = variant.map((variantPrice) => {
    // Find matching part data for this variant
    const optionPart = partsLookup.get(variantPrice.price_id);

    const description = variantPrice?.description;
    const thumbnail = selectedParentId
      ? optionData?.thumbnail
      : variantPrice?.thumbnail;

    let media = variantPrice.gallery || [];
    if (isDuplicate) {
      media = media.map((m) => ({
        ...m,
        id: 'duplicate',
      }));
    }

    const variantData = {
      id: isDuplicate ? null : variantPrice.id,
      name: variantPrice?.name || '',
      description: description || '',
      quote_description: variantPrice.quote_description || '',
      brochure_link: variantPrice.brochure_link || '',
      video_link: variantPrice.video_link || '',
      status: 'active',
      selectedPriceId: variantPrice.price_id || null,
      part_number: optionPart.part_number || '',
      temp_pn: optionPart.temp_pn || null,
      thumbnail: thumbnail || null,
      media,
      parent_option: selectedParentId || variantPrice.parent_option || false,
      price_id: variantPrice.price_id || selectedParentId || null,
      hasQuote: variantPrice.hasQuote || false,
      hasProduct: variantPrice.hasProduct || false,
    };
    const overrides = selectedParentId
      ? createOverridesFromComparison(variantData, processedOption)
      : variantPrice.overrides;

    if (overrides) {
      variantData.overrides = overrides;
    }

    if (variantPrice.hasQuote) {
      variantData.hasQuote = variantPrice.hasQuote;
    }

    if (variantPrice.hasProduct) {
      variantData.hasProduct = variantPrice.hasProduct;
    }

    return variantData;
  });

  return {
    option: processedOption,
    prices,
    selectedPriceIds,
    variant: variantDefaults,
    originalVariants: variantDefaults,
    remove: [],
  };
};

/**
 * Process form data for submission - handles file uploads and organizes data for API
 * @param {object} values - Form values containing option and variant data
 * @param {string} type - Type of operation ('add' or 'edit')
 * @returns {Promise<object>} Processed submit data with prices and option ready for API
 */
export const processSubmitData = async (values, type = 'add') => {
  // Initialize return structure for API submission
  const submitValues = {
    prices: { add: [], update: [], remove: [] },
    option: {},
  };

  // Destructure form values
  const { option, variant = [], originalVariants = [], remove = [] } = values;

  // Always create parent option price - works for both create and edit operations
  const parentOptionPrice = {
    price_id: option.selectedPriceId,
    name: option.name,
    description: option.description,
    quote_description: option.quote_description,
    brochure_link: option.brochure_link,
    video_link: option.video_link,
    thumbnail: await processThumbnailUpload(option.thumbnail), // Upload thumbnail if needed
    media:
      option?.media?.length > 0
        ? await processGalleryUpload(option.media)
        : null, // Upload gallery if needed
    parent_option: true, // Mark as parent option
    is_default: true,
  };

  // If editing, preserve the price link ID
  if (option.option_price_link_id) {
    parentOptionPrice.id = option.option_price_link_id;
    delete option.option_price_link_id; // Remove from option object to avoid duplication
  }

  // Sync processed media back to option object (for consistency)
  if (option.thumbnail && parentOptionPrice.thumbnail) {
    option.thumbnail = parentOptionPrice.thumbnail;
  }

  if (!parentOptionPrice.thumbnail) {
    delete option.thumbnail;
  }

  if (option.media && parentOptionPrice.media) {
    option.media = parentOptionPrice.media;
  }

  if (!parentOptionPrice.media) {
    delete option.media;
  }

  // Process variants with reduce to handle ADD/UPDATE logic efficiently
  const { addVariants, updateVariants } = await variant.reduce(
    async (accPromise, vData) => {
      const acc = await accPromise;

      const variantData = {
        ...vData,
      };

      const {
        id = null,
        thumbnail,
        media,
        price_id: priceId,
        selectedPriceId,
        ...restOfValues
      } = variantData;

      delete variantData.hasProduct;
      delete variantData.hasQuote;
      delete variantData.temp_pn;
      delete variantData.partNumber;

      // Skip variants marked for removal
      if (id && remove.includes(id)) {
        return acc;
      }

      // Prepare variant data for submission
      // Use selectedPriceId if available, otherwise fall back to priceId
      const returnData = {
        ...restOfValues,
        price_id: selectedPriceId || priceId,
        parent_option: false, // Mark as variant (not parent)
        is_default: false,
      };

      // Handle file uploads for variant
      returnData.thumbnail = await processThumbnailUpload(thumbnail);
      returnData.media = await processGalleryUpload(media);

      // Filter out empty/null values to clean up API payload
      const processedVariant = Object.fromEntries(
        Object.entries(returnData).filter(
          ([, value]) =>
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== '<p><br></p>', // Filter out empty rich text content
        ),
      );

      if (!id) {
        // No ID = new variant to ADD
        acc.addVariants.push(processedVariant);
      } else {
        // Has ID = existing variant, check if it changed for UPDATE
        const originalVariant = originalVariants.find((v) => v.id === id) || {};

        // Compare original values with new values using predefined keys
        let hasChanged = KEYS_TO_COMPARE.some((key) => {
          const originalValue = originalVariant[key] ?? null;
          const newValue = processedVariant[key] ?? null;
          return areValuesEqual(originalValue, newValue, key) === false;
        });

        const overrides = processedVariant.overrides ?? [];

        Object.keys(parentOptionPrice).forEach((key) => {
          if (
            !overrides.includes(key) &&
            key !== 'id' &&
            key !== 'parent_option' &&
            key !== 'is_default'
          ) {
            if (key === 'price_id' && overrides.includes('selectedPriceId')) {
              return;
            }
            processedVariant[key] = parentOptionPrice[key];
            hasChanged = true;
          }
          if (key === 'thumbnail' && !overrides.includes('thumbnail')) {
            processedVariant.thumbnail = 'default';
          }
        });

        // Only add to update list if something actually changed
        if (hasChanged) {
          // Add ID to the processed variant for update
          const variantWithId = { ...processedVariant, id };
          acc.updateVariants.push(variantWithId);
        }
      }

      return acc;
    },
    Promise.resolve({ addVariants: [], updateVariants: [] }),
  );

  // Organize prices by operation type based on add/edit mode
  submitValues.prices = {
    add: type === 'add' ? [parentOptionPrice, ...addVariants] : addVariants,
    update: type === 'edit' ? [parentOptionPrice, ...updateVariants] : [],
    remove,
  };

  // Delete media from option as there is a length restriction on string field
  if (option.media) {
    delete option.media;
  }

  // Copy option data (excluding processed fields)
  submitValues.option = { ...option };

  return submitValues;
};

/**
 * Get thumbnail URL from various thumbnail formats - handles different thumbnail data structures
 * @param {object|string} thumbnail - Thumbnail object with file data or URL string
 * @returns {string|object} Thumbnail URL or original thumbnail object
 */
export const getThumbnailType = (thumbnail) => {
  let thumbnailUrl;

  if (thumbnail?.file) {
    // New uploaded file - return base64 data
    thumbnailUrl = thumbnail.file; // Base64 string from uploaded file
  } else if (typeof thumbnail === 'string') {
    // Existing thumbnail URL
    thumbnailUrl = thumbnail; // Direct URL string
  } else {
    // Fallback - return as-is (could be object or other format)
    thumbnailUrl = thumbnail;
  }

  return thumbnailUrl;
};

// Export main function as default
export default processOptionData;
